import React from "react";
import { Button, Popconfirm, message } from "antd";
import { CompleteCallbackButton } from "../../CustomerDemo/components/CallWidgets";
import { AftercareCheckButton } from "../../CustomerDemo/components/TaskWidgets";
import dayjs from "dayjs";
import {
    AlertTriangle, Check, Phone, CalendarPlus, CalendarCheck, MessageSquare, UserCheck, UserX, Sparkles, Crown,
    PhoneCall, Clock, CalendarDays, Megaphone,
} from "lucide-react";
import {
    callCenterApi, customer360, formatMoney, journeyOf, needsEscalation, useCallCenter, type Customer, type NextAction,
} from "../../CustomerDemo/callCenterStore";
import { meName, softphone } from "../../CustomerDemo/softphone";

interface Props {
    customer: Customer;
    onBook: () => void;
    /** Mở tab "Lịch sử công việc" để ghi nhận tin nhắn Zalo/SMS */
    onOpenCare: () => void;
}

const ago = (t: number | null) => {
    if (!t) return "Chưa liên hệ";
    const d = dayjs().startOf("day").diff(dayjs(t).startOf("day"), "day");
    return d <= 0 ? `Hôm nay ${dayjs(t).format("HH:mm")}` : d === 1 ? "Hôm qua" : `${d} ngày trước`;
};

function Stat({ icon, label, value, hint }: { icon: React.ReactNode; label: string; value: React.ReactNode; hint?: React.ReactNode }) {
    return (
        <div className="flex items-start gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-500 flex items-center justify-center shrink-0">{icon}</div>
            <div className="min-w-0">
                <div className="text-[11px] text-gray-500">{label}</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{value}</div>
                {hint && <div className="text-[11px] text-gray-400 truncate">{hint}</div>}
            </div>
        </div>
    );
}

function NextActionButtons({ next, customer, onBook, onOpenCare }: { next: NextAction; customer: Customer } & Omit<Props, "customer">) {
    const s = useCallCenter();
    const phone = customer.phones[0];
    const by = meName();
    switch (next.type) {
        case "medical":
            return (
                <>
                    <Button type="primary" danger icon={<Phone size={14} />} onClick={() => softphone.dial(phone, { callbackId: next.refId })}>Gọi khách ngay</Button>
                    <CompleteCallbackButton id={next.refId!} buttonProps={{ size: "middle" }}>Bác sĩ đã xử lý</CompleteCallbackButton>
                </>
            );
        case "complaint": {
            const cb = s.callbacks.find(x => x.id === next.refId);
            return (
                <>
                    <Button type="primary" danger icon={<Phone size={14} />} onClick={() => softphone.dial(phone, { callbackId: next.refId })}>Gọi xử lý</Button>
                    {cb && needsEscalation(cb) && <Button danger onClick={() => { callCenterApi.escalateComplaint(cb.id, by); message.success('Đã chuyển quản lý xử lý'); }}>Chuyển quản lý</Button>}
                    <CompleteCallbackButton id={next.refId!} complaint buttonProps={{ size: "middle" }}>Đã xử lý</CompleteCallbackButton>
                </>
            );
        }
        case "first_call":
        case "call":
        case "remind":
            return <Button type="primary" icon={<Phone size={14} />} onClick={() => softphone.dial(phone)}>{next.type === "remind" ? "Gọi nhắc lịch" : "Gọi ngay"}</Button>;
        case "switch_channel":
            return (
                <>
                    <Button type="primary" icon={<MessageSquare size={14} />} onClick={onOpenCare}>Ghi nhận nhắn Zalo/SMS</Button>
                    <Button icon={<Phone size={14} />} onClick={() => softphone.dial(phone)}>Vẫn gọi</Button>
                </>
            );
        case "book":
            return (
                <>
                    <Button type="primary" icon={<CalendarPlus size={14} />} onClick={onBook}>Đặt lịch hẹn</Button>
                    <Button icon={<Phone size={14} />} onClick={() => softphone.dial(phone)}>Gọi tư vấn</Button>
                </>
            );
        case "confirm":
            return (
                <>
                    <Button type="primary" icon={<CalendarCheck size={14} />} onClick={() => { callCenterApi.setAppointmentStatus(next.refId!, "confirmed", by); message.success("Đã xác nhận lịch hẹn"); }}>Khách đã xác nhận</Button>
                    <Button icon={<Phone size={14} />} onClick={() => softphone.dial(phone)}>Gọi xác nhận</Button>
                </>
            );
        case "update_result":
            return (
                <>
                    <Button type="primary" icon={<UserCheck size={14} />} onClick={() => {
                        const n = callCenterApi.setAppointmentStatus(next.refId!, "done", by);
                        message.success(`Đã ghi nhận khách đến làm · tạo ${n} việc chăm sóc (hỏi thăm + khảo sát) và hoá đơn chờ thu tiền`);
                    }}>Khách đã đến làm</Button>
                    <Popconfirm title="Ghi nhận khách không đến?" description="Hệ thống sẽ tạo hẹn gọi lại sáng mai để xếp lịch khác." okText="Không đến" cancelText="Huỷ"
                        onConfirm={() => { callCenterApi.setAppointmentStatus(next.refId!, "no_show", by); message.success("Đã ghi nhận không đến · tạo hẹn gọi lại"); }}>
                        <Button danger icon={<UserX size={14} />}>Không đến</Button>
                    </Popconfirm>
                </>
            );
        case "aftercare":
            return (
                <>
                    <Button type="primary" icon={<PhoneCall size={14} />} onClick={() => softphone.dial(phone, { callbackId: next.refId })}>Gọi chăm sóc</Button>
                    <AftercareCheckButton customer={customer} callback={s.callbacks.find(x => x.id === next.refId)} buttonProps={{ size: "middle" }}>Điền phiếu hỏi thăm</AftercareCheckButton>
                </>
            );
        case "upsell":
            return (
                <>
                    <Button type="primary" icon={<CalendarPlus size={14} />} onClick={onBook}>Đặt lịch tái khám</Button>
                    <Button icon={<Phone size={14} />} onClick={() => softphone.dial(phone)}>Gọi tư vấn</Button>
                </>
            );
        default:
            return null;
    }
}

/** Cảnh báo y khoa + hành trình khách hàng + việc nên làm tiếp theo + tổng quan 360 */
export default function CustomerJourney({ customer, onBook, onOpenCare }: Props) {
    const s = useCallCenter();
    const { steps, current, next, streak } = journeyOf(s, customer);
    const k = customer360(s, customer);

    return (
        <div className="space-y-3">
            {customer.medicalNote && (
                <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900 px-4 py-2.5 flex items-start gap-2.5">
                    <AlertTriangle size={18} className="text-red-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-red-700 dark:text-red-300"><b>Lưu ý y khoa:</b> {customer.medicalNote}</div>
                </div>
            )}

            <div className="bg-white dark:bg-[#1f2937] border border-[#e5e7eb] dark:border-gray-700 rounded-lg p-4 space-y-4">
                {/* Hành trình */}
                <div className="overflow-x-auto overflow-y-hidden -mx-1 px-1 pb-1">
                    <ol className="flex items-start min-w-[640px]">
                        {steps.map((st, i) => (
                            <li key={st.key} className="flex-1 flex flex-col items-center relative">
                                {i > 0 && <span className={`absolute top-3.5 right-1/2 w-full h-0.5 ${st.done ? "bg-[#3e91ab]" : "bg-gray-200 dark:bg-gray-700"}`} />}
                                <span className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${i === current ? "bg-[#3e91ab] border-[#3e91ab] text-white ring-4 ring-[#3e91ab]/20" : st.done ? "bg-[#3e91ab] border-[#3e91ab] text-white" : "bg-white dark:bg-gray-900 border-gray-300 text-gray-400"}`}>
                                    {st.done && i !== current ? <Check size={14} /> : i + 1}
                                </span>
                                <span className={`mt-1.5 text-xs text-center ${i === current ? "font-bold text-[#3e91ab]" : st.done ? "text-gray-700 dark:text-gray-200" : "text-gray-400"}`}>{st.label}</span>
                                <span className="text-[10px] text-gray-400 h-3.5">{st.done && st.at ? dayjs(st.at).format("DD/MM") : ""}</span>
                            </li>
                        ))}
                    </ol>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
                    {/* Việc nên làm tiếp theo */}
                    <div className={`xl:col-span-2 rounded-lg border p-3.5 ${next.urgent ? "border-rose-200 bg-rose-50/70 dark:bg-rose-950/20 dark:border-rose-900" : "border-[#3e91ab]/30 bg-[#3e91ab]/5"}`}>
                        <div className={`text-[11px] font-bold uppercase tracking-wide mb-1 flex items-center gap-1.5 ${next.urgent ? "text-rose-600" : "text-[#3e91ab]"}`}>
                            <Sparkles size={12} /> Bước tiếp theo{next.urgent ? " · cần làm ngay" : ""}
                        </div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100">{next.title}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">{next.detail}</div>
                        <div className="flex flex-wrap gap-2 mt-3">
                            <NextActionButtons next={next} customer={customer} onBook={onBook} onOpenCare={onOpenCare} />
                        </div>
                    </div>

                    {/* Tổng quan 360 */}
                    <div className="xl:col-span-3 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3 content-center">
                        <Stat icon={<Crown size={15} />} label="Hạng khách" value={<span className={`px-2 py-0.5 rounded text-xs font-bold ${k.tier.cls}`}>{k.tier.label}</span>}
                            hint={<>Đã chi {formatMoney(k.spend)}{k.debt > 0 && <span className="text-rose-500"> · nợ {formatMoney(k.debt)}</span>}</>} />
                        <Stat icon={<PhoneCall size={15} />} label="Cuộc gọi" value={`${k.calls} cuộc · ${k.outCalls ? `${k.answerRate}% nghe máy` : "chưa gọi ra"}`}
                            hint={streak > 0 ? <span className="text-rose-500">{streak} lần gần nhất không nghe</span> : k.outCalls ? `Tỉ lệ tính trên ${k.outCalls} cuộc gọi ra` : undefined} />
                        <Stat icon={<Clock size={15} />} label="Liên hệ gần nhất" value={ago(k.lastContact)} />
                        <Stat icon={<CalendarDays size={15} />} label="Lịch hẹn" value={`${k.appts} lịch · ${k.done} đã đến`} hint={k.cancelled || k.noShow ? `${k.cancelled} huỷ · ${k.noShow} không đến` : undefined} />
                        <Stat icon={<UserCheck size={15} />} label="Lần đến gần nhất" value={k.lastVisit ? dayjs(k.lastVisit).format("DD/MM/YYYY") : "Chưa đến"} />
                        <Stat icon={<Megaphone size={15} />} label="Nguồn khách" value={customer.source || "Chưa rõ"} hint={customer.referrer ? `Giới thiệu: ${customer.referrer}` : undefined} />
                    </div>
                </div>
            </div>
        </div>
    );
}
